import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Stack,
  Icon,
  Link,
  Button,
  useColorModeValue,
  VStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { FaBook, FaCalculator, FaChartBar, FaFileAlt, FaLightbulb, FaPlayCircle, FaDownload } from 'react-icons/fa';
import Placeholder from '../components/Placeholder';

const ResourceCard = ({ title, description, icon, link }) => {
  return (
    <Stack
      bg={useColorModeValue('white', 'gray.800')}
      p={8}
      rounded="xl"
      shadow="lg"
      borderWidth="1px"
      borderColor={useColorModeValue('gray.100', 'gray.700')}
      align="flex-start"
      spacing={4}
      _hover={{
        transform: 'translateY(-5px)',
        shadow: 'xl',
        borderColor: 'brand.200',
      }}
      transition="all 0.3s"
    >
      <Placeholder
        height="160px"
        text={title}
        icon={icon}
        bg={useColorModeValue('brand.50', 'gray.700')}
        iconColor="brand.400"
      />
      <Stack spacing={2}>
        <Heading size="md">{title}</Heading>
        <Text color={useColorModeValue('gray.600', 'gray.300')}>
          {description}
        </Text>
      </Stack>
      <Button
        as={Link}
        href={link}
        variant="ghost"
        colorScheme="brand"
        size="sm"
        rightIcon={<FaDownload />}
      >
        Access Resource
      </Button>
    </Stack>
  );
};

const FAQ = ({ question, answer }) => {
  return (
    <AccordionItem border="none">
      <AccordionButton
        py={4}
        _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }}
      >
        <Box flex="1" textAlign="left" fontWeight="medium">
          {question}
        </Box>
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel pb={4} color={useColorModeValue('gray.600', 'gray.300')}>
        {answer}
      </AccordionPanel>
    </AccordionItem>
  );
};

const Resources = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  return (
    <Box>
      {/* Hero Section */}
      <Box bg={useColorModeValue('brand.500', 'gray.900')} color="white">
        <Container maxW="7xl" py={20}>
          <Stack spacing={6} maxW="3xl">
            <Heading
              as="h1"
              fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
              fontWeight="extrabold"
            >
              Resources & Learning Center
            </Heading>
            <Text fontSize={{ base: 'lg', md: 'xl' }} opacity={0.9}>
              Access our comprehensive collection of resources designed to help you excel in mutual fund distribution.
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* Resources Grid */}
      <Box py={20} bg={bgColor}>
        <Container maxW="7xl">
          <VStack spacing={16}>
            {/* Educational Resources */}
            <Stack spacing={8} width="full">
              <Heading size="xl">Educational Resources</Heading>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
                <ResourceCard
                  title="Beginner's Guide"
                  description="Comprehensive guide covering mutual fund basics and distribution essentials."
                  icon={FaBook}
                  link="/resources/beginners-guide.pdf"
                />
                <ResourceCard
                  title="Market Analysis Tools"
                  description="Tools and templates for analyzing market trends and fund performance."
                  icon={FaChartBar}
                  link="/resources/market-analysis.xlsx"
                />
                <ResourceCard
                  title="Client Presentation Templates"
                  description="Professional templates for client presentations and proposals."
                  icon={FaFileAlt}
                  link="/resources/presentation-templates.pptx"
                />
                <ResourceCard
                  title="Investment Calculator"
                  description="Interactive calculators for SIP, lump sum investments, and returns."
                  icon={FaCalculator}
                  link="/tools/calculator"
                />
                <ResourceCard
                  title="Video Tutorials"
                  description="Step-by-step video guides on using our platform effectively."
                  icon={FaPlayCircle}
                  link="/resources/tutorials"
                />
                <ResourceCard
                  title="Best Practices Guide"
                  description="Industry best practices and tips for successful fund distribution."
                  icon={FaLightbulb}
                  link="/resources/best-practices.pdf"
                />
              </SimpleGrid>
            </Stack>

            {/* FAQs */}
            <Stack spacing={8} width="full">
              <Heading size="xl">Frequently Asked Questions</Heading>
              <Box
                bg={useColorModeValue('white', 'gray.800')}
                rounded="xl"
                shadow="lg"
                p={8}
              >
                <Accordion allowMultiple>
                  <FAQ
                    question="How do I get started as a mutual fund distributor?"
                    answer="To become a mutual fund distributor, you need to register with AMFI and clear the NISM-Series-V-A exam. After registration, you can join our platform and we'll guide you through the onboarding process."
                  />
                  <FAQ
                    question="What support do you provide to new distributors?"
                    answer="We provide comprehensive support including training materials, marketing resources, client management tools, and dedicated customer support. Our team is always available to help you grow your business."
                  />
                  <FAQ
                    question="How are commissions calculated and paid?"
                    answer="Commissions are calculated based on the AUM (Assets Under Management) and the type of funds. We provide detailed commission statements and process payments monthly. You can track your earnings in real-time through our dashboard."
                  />
                  <FAQ
                    question="What tools are available for client management?"
                    answer="Our platform offers a suite of tools including CRM, portfolio tracking, investment calculators, and reporting tools. These help you manage client relationships effectively and provide better service."
                  />
                  <FAQ
                    question="How can I access market research and analysis?"
                    answer="We provide regular market updates, fund analysis reports, and research materials through our platform. You can access these resources in the 'Research' section of your dashboard."
                  />
                </Accordion>
              </Box>
            </Stack>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
};

export default Resources;
